import React from 'react';
import { Divider, Form, Col, Select } from 'antd';
import { object, string, bool, func, array, oneOfType, node as nodeElement } from 'prop-types';
import moment from 'moment';

import {
  truncate,
  getFullUrl,
  getAvatarUrl,
  filterOption,
  filteredUsers,
  disabledDates,
  getCurrentUserId,
  sortDataByAlphabet,
  isCurrentUserAssignable,
} from '../../../utils';
import { SvgIcon } from '../../../components/svg-icon';
import { ButtonWithLoader, AssignButton } from '../../../components/ui-helpers';
import { formatDate } from './helpers';
import { ROUTES } from '../../../routes';
import { TimePicker, Popover, DatePicker } from '../../../components/popovers';
import TaskPriority from '../../../components/task-item/task-priority';
import { theme } from '../../../styles';

import {
  BoldS,
  SelectS,
  LinkIcon,
  ContactS,
  ContactsS,
  OptionRow,
  AssignInfo,
  SvgWrapper,
  ButtonWrap,
  RequestLink,
  StyledInput,
  OptionHeader,
  FieldWrapper,
  StyledOption,
  TaskInfoWrap,
  UserImageWrap,
  StyledFormItem,
  SelectDropdown,
  OptionContainer,
} from './styles';
import { ReactQuillWrapped } from '../../../components/react-quill';
import { TASK_PRIORITIES } from '../../../api/constants';

const { Option } = Select;

export const TaskForm = ({
  form: { getFieldDecorator, setFieldsValue },
  handleRequestSearch,
  isFetchingRequests,
  setSelectedRequest,
  isWithoutRequest,
  searchedRequests,
  requestAssignees,
  selectedRequest,
  setRequestsData,
  setSelectedUser,
  actionButtons,
  selectedUser,
  description,
  taskPriority,
  isUpdating,
  onSubmit,
  dueDate,
  dueTime,
  title,
  users,
}) => (
  <Form onSubmit={onSubmit}>
    <TaskInfoWrap>
      {!isUpdating && isWithoutRequest &&
        <StyledFormItem
          colon={false}
          label="Request"
        >
          {getFieldDecorator('requestId', {
            initialValue: selectedRequest,
          })(<SelectS
            size="large"
            showSearch
            allowClear
            showArrow={false}
            filterOption={false}
            loading={isFetchingRequests}
            onChange={setSelectedRequest}
            onSearch={handleRequestSearch}
            defaultActiveFirstOption={false}
            onBlur={() => setRequestsData()}
            getPopUpContainer={node => node.parentElement}
            placeholder="Search by description or subject or contact's full name, emails..."
            notFoundContent={isFetchingRequests ? null : 'Not Found'}
            dropdownRender={(menu) => {
              if (!isFetchingRequests && menu.props.inputValue.length > 1) {
                return (
                  <SelectDropdown>
                    {menu}
                  </SelectDropdown>
                );
              }
              return (<SelectDropdown />);
            }}
          >
            {searchedRequests.map(item => (
              <Option key={item._id} value={item._id}>
                <OptionContainer>
                  <Popover
                    trigger="hover"
                    placement="bottom"
                    overlayStyle={{ zIndex: 1200 }}
                    content={
                      <div>
                        <OptionHeader>
                          <span>Subject: <BoldS>{truncate(item.subject)}</BoldS></span>
                          <RequestLink target="_blank" href={getFullUrl(`${ROUTES.REQUEST}?id=${item._id}`)}>
                            Open request
                            <LinkIcon type="openLink" />
                          </RequestLink>
                        </OptionHeader>
                        <ContactsS>
                          Contacts:
                          {item.contacts.map(contact =>
                            <ContactS key={contact._id}>{contact.fullName}</ContactS>)
                          }
                        </ContactsS>
                        <OptionRow>
                          <span>Request date: <BoldS>{formatDate(item.requestDate)}</BoldS></span>
                        </OptionRow>
                      </div>
                    }
                  >
                    {item.subject}
                  </Popover>
                </OptionContainer>
              </Option>
            ))}
          </SelectS>)}
        </StyledFormItem>
      }
      <StyledFormItem
        colon={false}
        label="Title"
      >
        {getFieldDecorator('title', {
          initialValue: title,
          rules: [{
            required: true, message: 'This field is required!',
          }, {
            max: 200, message: 'Max length is 200 characters.',
          }],
        })(<StyledInput.TextArea
          rows={1}
          placeholder="Write a task title here..."
          size="large"
        />)}
      </StyledFormItem>

      <StyledFormItem
        colon={false}
        label="Description"
      >
        {getFieldDecorator('description', {
          initialValue: description,
          rules: [{
            max: 5000, message: 'Max length is 5000 characters.',
          }],
        })(<ReactQuillWrapped
          height="300px"
          withPaddings
          placeholder="Write a task description here..."
          theme="snow"
          scrollingContainer="#description"
          bounds="#description"
        />)}
      </StyledFormItem>
      <StyledFormItem
        colon={false}
        label="Priority"
      >
        {getFieldDecorator('taskPriority', {
          initialValue: taskPriority || 'NO_PRIORITY',
        })(<SelectS getPopupContainer={triggerNode => triggerNode.parentNode}>
          {TASK_PRIORITIES.map(priority => (
            <StyledOption value={priority.value} key={priority.value}>
              <TaskPriority priority={priority} />
            </StyledOption>
          ))}
        </SelectS>)
        }
      </StyledFormItem>
      <FieldWrapper>
        <Popover
          placement="top"
          arrowPointAtCenter
          trigger="hover"
          onClick={e => e.stopPropagation()}
          content={
            <AssignInfo onClick={e => e.stopPropagation()}>
              You can assign Sales Agent only if he is assigned to Request.
            </AssignInfo>
          }
        >
          <SvgWrapper>
            <SvgIcon type="info" color={theme.colors.secondaryDark} fillOpacity={1} />
          </SvgWrapper>
        </Popover>
        <AssignButton
          type="button"
          disabled={selectedUser === getCurrentUserId()
            || !isCurrentUserAssignable(isWithoutRequest, requestAssignees, users)
          }
          onClick={() => {
            setSelectedUser(getCurrentUserId());
            setFieldsValue({ userId: getCurrentUserId() });
          }}
        >
          <SvgIcon type="plus" /> Assign me
        </AssignButton>

        <StyledFormItem
          colon={false}
          label="Assignee"
        >
          {getFieldDecorator('userId', {
            initialValue: selectedUser,
            rules: [{
              required: false, message: 'Please choose responsible user...',
            }],
          })(<SelectS
            showSearch
            size="large"
            allowClear
            placeholder="Choose responsible User..."
            defaultActiveFirstOption={false}
            onChange={setSelectedUser}
            filterOption={filterOption}
            getPopupContainer={triggerNode => triggerNode.parentNode}
          >
            {sortDataByAlphabet('fullName', filteredUsers(isWithoutRequest, requestAssignees, users)).map(user => (
              <StyledOption
                key={user._id}
                value={user._id}
                hidden={!user.isActivated}
              >
                <UserImageWrap
                  width={24}
                  height={24}
                  src={getAvatarUrl(user.profileImage)}
                />
                {user.fullName} <Divider type="vertical" /> {user.email}
              </StyledOption>
            ))}
          </SelectS>)}
        </StyledFormItem>
      </FieldWrapper>

      <Col span={12}>
        <StyledFormItem
          colon={false}
          label="Due Date"
          style={{ paddingRight: '24px' }}
        >
          {getFieldDecorator('dueDate', {
            initialValue: isUpdating ? dueDate : null,
            rules: [{
              required: true, message: 'Please select a due date.',
            }],
          })(<DatePicker
            placeholder="Select Due Date..."
            disabledDate={disabledDates}
          />)}
        </StyledFormItem>
      </Col>

      <Col span={12}>
        <StyledFormItem
          colon={false}
          label="Due Time"
        >
          {getFieldDecorator('dueTime', {
            initialValue: dueTime && moment(dueTime),
          })(<TimePicker
            format="HH:mm"
            placeholder="HH:MM"
          />)}
        </StyledFormItem>
      </Col>

    </TaskInfoWrap>

    {!actionButtons ?
      <ButtonWrap>
        <ButtonWithLoader type="submit">
          Save
        </ButtonWithLoader>
      </ButtonWrap> :
      actionButtons}
  </Form>
);

TaskForm.propTypes = {
  selectedRequest: oneOfType([object, array, string]).isRequired,
  handleRequestSearch: func.isRequired,
  isFetchingRequests: bool.isRequired,
  setSelectedRequest: func.isRequired,
  searchedRequests: array.isRequired,
  isWithoutRequest: bool.isRequired,
  setRequestsData: func.isRequired,
  setSelectedUser: func.isRequired,
  taskPriority: string.isRequired,
  description: string.isRequired,
  dueDate: object.isRequired,
  isUpdating: bool.isRequired,
  onSubmit: func.isRequired,
  title: string.isRequired,
  requestAssignees: array,
  form: object.isRequired,
  selectedUser: string,
  dueTime: string,
  users: object,
  actionButtons: nodeElement,
};

TaskForm.defaultProps = {
  selectedUser: undefined,
  requestAssignees: [],
  dueTime: undefined,
  users: {},
  actionButtons: null,
};



// WEBPACK FOOTER //
// ./src/modals/tasks-modal/task-form/index.js